/**
 * React Query hooks for Accounting Connectors
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { accountingConnectorsAPI } from '../services/accounting-connectors.service';
import type { CreateConnectorInput } from '../types/accounting-connectors.types';

const CONNECTOR_KEY = 'accounting-connector';

// ============ QUERIES ============

export function useConnectorConfig() {
  return useQuery({
    queryKey: [CONNECTOR_KEY, 'config'],
    queryFn: () => accountingConnectorsAPI.getConfig(),
    staleTime: 30000,
  });
}

export function useConnectorLogs() {
  return useQuery({
    queryKey: [CONNECTOR_KEY, 'logs'],
    queryFn: () => accountingConnectorsAPI.getLogs(),
    staleTime: 30000,
  });
}

// ============ MUTATIONS ============

export function useSaveConnectorConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (config: CreateConnectorInput) =>
      accountingConnectorsAPI.saveConfig(config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CONNECTOR_KEY, 'config'] });
    },
  });
}

export function useTestConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => accountingConnectorsAPI.testConnection(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CONNECTOR_KEY, 'config'] });
    },
  });
}

export function useSyncConnector() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => accountingConnectorsAPI.sync(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CONNECTOR_KEY] });
    },
  });
}
